import _ from 'lodash';
import {strRight, weakEqual} from '@packages/utilities/string-utils';
import {
  type Font,
  type FontAxis,
  DEFAULT_FONTS,
  FONT_SOURCE_CUSTOM,
  FONT_SOURCE_GOOGLE,
  FONT_SOURCE_GOOGLE_CUSTOM,
  FONT_SOURCE_TYPEKIT,
  REGISTERED_AXIS_ITAL,
  REGISTERED_AXIS_OPSZ,
  REGISTERED_AXIS_SLNT,
  REGISTERED_AXIS_WDTH,
  REGISTERED_AXIS_WGHT,
} from '@packages/systems/core/constants/Fonts';

import type {
  CustomFontFallbacksType,
  GoogleFontType,
  OtherFontType,
  TypeKitFontType,
  RemoteGoogleFont,
} from './FontUtilsTypes';

/**
 * Determines whether a font is a Google font
 * - "GOOGLE" => A default font that is a google font
 * - "GOOGLE_CUSTOM" => A font added via the google integration
 * Both sources are considered Google fonts as they're both
 * fetched from the Google Fonts API.
 */
export function isGoogleFont({source}: Font): boolean {
  return source === FONT_SOURCE_GOOGLE || source === FONT_SOURCE_GOOGLE_CUSTOM;
}

const singleQuoteRegex = new RegExp(/^'.*'$/);
const doubleQuoteRegex = new RegExp(/^".*"$/);
const lettersDashesAndNumbersRegex = new RegExp(/^[a-zA-Z0-9-]+$/);

function wrapSingleFontNameWithQuotes(input: string): string {
  const isQuoted = singleQuoteRegex.test(input) || doubleQuoteRegex.test(input);
  const isSimple = lettersDashesAndNumbersRegex.test(input);

  if (isQuoted || isSimple) {
    return input;
  } else {
    return `'${input}'`;
  }
}

/**
 * Splits a font-family list on top-level commas, treating everything inside
 * a `var(...)` (or other function) call as part of a single entry. This
 * keeps a fallback comma inside `var(--a, var(--b))` from being mistaken
 * for a list separator.
 */
function splitFontFamilyNames(input: string): string[] {
  const names: string[] = [];
  let depth = 0;
  let start = 0;

  for (let i = 0; i < input.length; i++) {
    const char = input.charAt(i);
    if (char === '(') {
      depth++;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    } else if (char === ',' && depth === 0) {
      names.push(input.slice(start, i));
      start = i + 1;
    }
  }
  names.push(input.slice(start));

  return names;
}

const handleComplexNames = (input: string): string => {
  if (!input) {
    return input;
  }

  return splitFontFamilyNames(input)
    .map((rawName) => {
      const name = rawName.trim();
      // A `var(...)` reference resolves to a font stack (including any
      // fallbacks) at the CSS level, so it's kept as-is rather than quoted
      // like a literal font name.
      return name.toLowerCase().startsWith('var(')
        ? name
        : wrapSingleFontNameWithQuotes(escapeInternalQuotes(name));
    })
    .join(', ');
};

const escapeInternalQuotes = (input: string): string => {
  if (!input) {
    return input;
  }

  return input.replace(
    /^(['"]?)(.*?)(['"]?)$/,
    (match, leadingQuote, captureGroup, trailingQuote) => {
      const withEscapedQuotes = captureGroup
        .replace(/([^\\])"/g, '$1\\"')
        .replace(/([^\\])'/g, "$1\\'");

      return `${leadingQuote}${withEscapedQuotes}${trailingQuote}`;
    }
  );
};

// this will add all fallback fonts to the font-family string if one were specified
// for example, for custom fonts fallback fonts are specified in the project dashboard
export const safeFamilyName = (
  family: string,
  fonts: Font[] = DEFAULT_FONTS
) => {
  if (family && family.indexOf(',') > 0) {
    return handleComplexNames(family);
  }

  const fontDetails = _.find(fonts, function (font) {
    return weakEqual(family, font.family);
  });

  if (fontDetails) {
    const parts: Array<
      any | string | 'cursive' | 'monospace' | 'sans-serif' | 'serif'
    > = [];
    // Add the main font family, e.g. 'Times New Roman'
    parts.push(handleComplexNames(family));

    // Add each fallback fonts, e.g. Times
    _.each(fontDetails.fallback, function (fallbackFont) {
      parts.push(handleComplexNames(fallbackFont));
    });

    // Add the main font type at the end, e.g. serif
    parts.push(fontDetails.type);

    return _.compact(parts).join(', ');
  }

  return handleComplexNames(family);
};

const processOtherFonts = (
  otherFonts: OtherFontType[],
  fallbacks: CustomFontFallbacksType
): Font[] => {
  // Bucket by (origin, fontFamily) so the same family from different sources
  // (site / library A / library B) stays as separate Font rows. Group by each
  // part of the origin, then by family — no composite key to serialize.
  const byOrigin = _.flatMap(
    _.groupBy(otherFonts, (font) => font.origin?.[0]),
    (sameSource) => _.values(_.groupBy(sameSource, (font) => font.origin?.[1]))
  );
  const families = _.flatMap(byOrigin, (sameOrigin) =>
    _.values(_.groupBy(sameOrigin, 'fontFamily'))
  );
  // @ts-expect-error - TS2322 - lodash `_.property` widens weights/axes to unknown, so the row shape isn't assignable to Font[].
  return _.map(families, function (fonts) {
    const fontFamilyName = _.head(fonts)?.fontFamily ?? '';
    const files = _.map(fonts, 'file');
    const fontWeights = fonts.map(_.property('weight'));
    const variableAxes = fonts.map(_.property('axes'));
    const hasItalic = Boolean(_.find(fonts, _.property('italic')));
    const members = _.map(fonts, function (font) {
      return {
        weight: font.weight,
        files,
        italic: font.italic,
      };
    });

    const fontOrigins = fonts.map(_.property('origin'));
    const origin = _.head(fontOrigins);

    return {
      family: fontFamilyName,
      label: fontFamilyName,
      source: FONT_SOURCE_CUSTOM,
      type: 'sans-serif',
      weights: fontWeights,
      axes: _.head(variableAxes),

      italic: hasItalic,
      members,
      fallback: fallbacks ? [fallbacks[fontFamilyName]] : [],
      ...(origin ? {origin} : {}),
    };
  });
};

/**
 * Put in <option>s into font, font-weight, and other dropdowns (since they rely on server data).
 */
export const formatAllFonts = ({
  googleFonts,
  typeKitFonts,
  otherFonts,
  customFontFallbacks,
}: {
  googleFonts: GoogleFontType[];
  typeKitFonts: TypeKitFontType[];
  otherFonts: OtherFontType[];
  customFontFallbacks: CustomFontFallbacksType;
}): Font[] => {
  const allFonts: Font[] = [];

  _.each(googleFonts, function (font) {
    const defaultedFontWeight =
      !font.weight || font.weight.length < 1 ? ['400'] : font.weight;
    const weights: Array<number> = [];
    const italic: Array<number> = [];
    _.each(defaultedFontWeight, function (weightString) {
      // Transform Google font weight to Webflow compatible font weight.
      const match = weightString.match(/^([0-9]+|regular)?(italic)?$/);
      if (match) {
        const weight =
          !match[1] || match[1] === 'regular' ? 400 : parseInt(match[1], 10);
        const isItalic = Boolean(match[2]);
        if (isItalic) {
          italic.push(weight);
        } else {
          weights.push(weight);
        }
      } else {
        console.warn('could not parse google font', weightString);
      }
    });

    allFonts.push({
      family: font.name,
      // @ts-expect-error - TS2345 - Argument of type '{ family: string; label: string; source: "Google-Custom"; type: "sans-serif"; weights: number[]; subsets: string[]; italic: number[]; fallback: never[]; }' is not assignable to parameter of type 'Font'.
      label: font.name,
      source: FONT_SOURCE_GOOGLE_CUSTOM,
      type: 'sans-serif',
      weights,
      subsets: font.subsets,
      italic,
      fallback: [],
      ...(font.origin ? {origin: font.origin} : {}),
    });
  });

  _.each(typeKitFonts, function (font) {
    // @ts-expect-error - TS2339 - Property 'weight' does not exist on type 'TypeKitFontType'.
    font.weight = [];
    // @ts-expect-error - TS2339 - Property 'italic' does not exist on type 'TypeKitFontType'.
    font.italic = [];
    _.each(font.variations, function (v) {
      if (_.startsWith(v, 'n')) {
        const nw = strRight(v, 'n');
        // @ts-expect-error - TS2339 - Property 'weight' does not exist on type 'TypeKitFontType'.
        font.weight.push(parseInt(nw, 10) * 100);
      } else if (_.startsWith(v, 'i')) {
        const iw = strRight(v, 'i');
        // @ts-expect-error - TS2339 - Property 'italic' does not exist on type 'TypeKitFontType'.
        font.italic.push(parseInt(iw, 10) * 100);
      }
    });

    const family = font.cssNames.join(',');
    allFonts.push({
      family,
      // @ts-expect-error - TS2345 - Argument of type '{ family: string; label: string; source: "Typekit"; type: "sans-serif"; weights: any; italic: any; fallback: never[]; }' is not assignable to parameter of type 'Font'.
      label: font.name,
      source: FONT_SOURCE_TYPEKIT,
      type: 'sans-serif',
      // @ts-expect-error weight is not in the type because we did a dangerous mutation above!!!
      weights: font.weight,
      // @ts-expect-error italic is not in the type because we did a dangerous mutation above!!!
      italic: font.italic,
      fallback: [],
      ...(font.origin ? {origin: font.origin} : {}),
    });
  });

  const processedOtherFonts = processOtherFonts(
    otherFonts,
    customFontFallbacks
  );

  allFonts.push(...processedOtherFonts);

  return allFonts;
};

/**
 * Sort order to determine the prioritization of which axes
 * are added first. Normally used in conjunction with lodash.sortBy().
 */
export function axesSortOrder(axis: FontAxis): number {
  switch (axis.tag) {
    case REGISTERED_AXIS_WGHT: {
      return 0;
    }
    case REGISTERED_AXIS_WDTH: {
      return 1;
    }
    case REGISTERED_AXIS_OPSZ: {
      return 2;
    }
    case REGISTERED_AXIS_SLNT: {
      return 3;
    }
    case REGISTERED_AXIS_ITAL: {
      return 4;
    }
    default: {
      return 5;
    }
  }
}

const GOOGLE = 'Google';

const googleFonts = (fonts: Font[], includeCustom?: boolean) => {
  const _googleFonts = _.filter(fonts, function (font) {
    return includeCustom
      ? font.source.indexOf(GOOGLE) > -1
      : font.source === GOOGLE;
  });

  return _googleFonts;
};

const getGoogleWebFontsUrl = (
  remoteGoogleFonts: Array<RemoteGoogleFont> | undefined,
  fonts: Font[]
) => {
  const families: Array<any | string> = [];
  const labels: Array<any | string> = [];

  _.each(remoteGoogleFonts, function (font) {
    families.push(font.name + ':400');
    labels.push(font.name);
  });

  // Find just the Google fonts
  _.each(googleFonts(fonts), function (font) {
    families.push(font.family + ':400');
    labels.push(font.family);
  });

  // Nothing to request — `?family=&text=` is a 400 from Google.
  if (!families.length && !labels.length) {
    return null;
  }

  // Generate a list of families (we only need the normal weight) and unique characters that we need
  // to populate the font family dropdown in the style editor.
  const fontsNeeded = families.join('|');
  const charsNeeded = _.uniq(labels.join('').split('')).join('');
  return `https://fonts.googleapis.com/css?family=${fontsNeeded}&text=${charsNeeded}`;
};

const createOrUpdateLink = (document: Document, id: string, url: string) => {
  const existing = document.getElementById(id);
  if (existing) {
    existing.setAttribute('href', url);
    return;
  }

  const link = document.createElement('link');
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('type', 'text/css');
  link.setAttribute('id', id);
  link.setAttribute('href', url);

  return document.head.appendChild(link);
};

const deleteLink = (document: Document, id: string) => {
  document.getElementById(id)?.remove();
};

export const DESIGNER_GOOGLE_WEB_FONTS_ELEMENT_ID = 'google-web-fonts';
export const loadGoogleWebFonts = (
  document: Document,
  // `initialize.ts` reads this off untyped remote data and passes it through
  // undefaulted, so the tolerance the lodash iteration already has belongs in
  // the signature.
  remoteGoogleFonts: Array<RemoteGoogleFont> | undefined,
  allFonts: Font[]
) => {
  const url = getGoogleWebFontsUrl(remoteGoogleFonts, allFonts);

  if (url === null) {
    deleteLink(document, DESIGNER_GOOGLE_WEB_FONTS_ELEMENT_ID);
    return;
  }

  createOrUpdateLink(document, DESIGNER_GOOGLE_WEB_FONTS_ELEMENT_ID, url);
};

export const fontVariations = function (
  fonts: {fontFamily: string; weight: string | number; italic: boolean}[]
) {
  const customFonts = fonts;
  return _.flatMap(customFonts, (font) => {
    const weight = font.weight || '400';
    const style = font.italic ? 'i' : 'n';
    return `${font.fontFamily}:${style}${weight}`;
  });
};

export const CANVAS_FONTS_ELEMENT_ID = 'canvas-fonts';

/**
 * Injects or updates a `<link rel="stylesheet">` for Google Fonts (and Google-Custom) faces
 * described by {@link fonts}, using the legacy css?family= API used across Designer canvas.
 */
export const loadGoogleFontsStylesheet = (
  document: Document,
  fonts: Font[],
  linkElementId: string
) => {
  const allSubsets: Array<any | string> = [];
  const _googleFonts = _.map(googleFonts(fonts, true), function (font) {
    const normal = font.weights;
    const italic = font.italic === 'same' ? normal : font.italic;
    const subsets = font.subsets;
    const weights: Array<any | number | string> = [];

    _.each(normal, function (weight) {
      weights.push(weight);
    });

    _.each(italic, function (weight) {
      const tag = weight + 'italic';
      weights.push(tag);
    });

    _.each(subsets, function (subset) {
      allSubsets.push(subset);
    });

    if (weights.length > 0) {
      return font.family + (':' + weights.join(','));
    } else {
      return null;
    }
  });

  const subsetStr = _.uniq(allSubsets).join(',');
  const fontsNeeded = _.compact(_googleFonts).join('|');
  const url =
    'https://fonts.googleapis.com/css?family=' +
    fontsNeeded +
    '&subset=' +
    subsetStr;

  createOrUpdateLink(document, linkElementId, url);
};

export const loadCanvasFonts = (document: Document, fonts: Font[]) => {
  loadGoogleFontsStylesheet(document, fonts, CANVAS_FONTS_ELEMENT_ID);
};

/**
 * Filter the site-fonts portion of `state.fonts` down to what we can prove
 * is installed on the current site. Library rows (any Font with `origin`)
 * always pass through untouched.
 */
export const filterAllowedFontsForInstalledOnlyPicker = (
  fonts: ReadonlyArray<Font>
): Font[] =>
  fonts.filter(
    (font) => Boolean(font.origin) || font.source !== FONT_SOURCE_GOOGLE
  );
